import React, { useState, useEffect, useRef } from 'react'
import { Form, Card, Space, Row, Col, Typography, Button } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCheckCircle,
  faXmarkCircle,
  faAllergies,
  faPills,
  faSyringe,
  faVirus,
  faUserGroup,
  faPersonRunning,
  faBrain,
  faStethoscope
} from '@fortawesome/free-solid-svg-icons'
import dayjs from 'dayjs'
import DesktopLifestyle from './DesktopLifestyle'
import DesktopAllergies from './DesktopAllergies'
import DesktopMentalHealth from './DesktopMentalHealth'
import DesktopChronicIllnesses from './DesktopChronicIllnesses'
import DesktopFamilyHistory from './DesktopFamilyHistory'
import DesktopCovidInfo from './DesktopCovidInfo'
import DesktopGeneralMeds from './DesktopGeneralMeds'
import DesktopSurgeries from './DesktopSurgeries'
import '../style/DesktopMedicalHistoryForm.css'
import DesktopMedicalHistoryLoadingOverlay from './LoadingOverlay'
import { useSelector } from 'react-redux'

const { Title, Text } = Typography

const DesktopMedicalHistoryForm = ({ data, fieldValues }) => {
  const [saving, setSaving] = useState(false)
  const [selectedFacet, setSelectedFacet] = useState(null)
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const patMedicalHistory = useSelector((state) => state.patMedHist.data) || {}
  const containerRef = useRef(null)

  useEffect(() => {
    if (containerRef.current && selectedFacet) {
      containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [selectedFacet])

  const medHistSections = [
    {
      title: 'Allergies',
      key: 'allergies',
      icon: faAllergies,
      color: '16, 185, 129',
      description: 'Food, medication, and environmental allergies'
    },
    {
      title: 'Chronic Illnesses',
      key: 'chronicIllnesses',
      icon: faStethoscope,
      color: '139, 92, 246',
      description: 'Long-term health conditions'
    },
    {
      title: 'General Medications',
      key: 'meds',
      icon: faPills,
      color: '245, 158, 11',
      description: 'Current and past medications'
    },
    {
      title: 'Covid Information',
      key: 'covidInfo',
      icon: faVirus,
      color: '16, 185, 129',
      description: 'Vaccination status and health history'
    },
    {
      title: 'Family History',
      key: 'familyHistory',
      icon: faUserGroup,
      color: '245, 158, 11',
      description: 'Hereditary conditions and family health'
    },
    {
      title: 'Lifestyle',
      key: 'lifestyle',
      icon: faPersonRunning,
      color: '236, 72, 153',
      description: 'Diet, exercise, and lifestyle habits'
    },
    {
      title: 'Mental Health',
      key: 'mentalHealth',
      icon: faBrain,
      color: '99, 102, 241',
      description: 'Mental wellness and psychological history'
    },
    {
      title: 'Surgical History',
      key: 'surgicalHistory',
      icon: faSyringe,
      color: '100, 116, 139',
      description: 'Previous surgeries and procedures'
    }
  ]

  const handleSave = async () => {
    setLoading(true)
    try {
      // Your save logic here
      setLoading(false)
    } catch (error) {
      console.error('Error saving:', error)
      setLoading(false)
    }
  }

  const renderMainView = () => {
    const completedSections = medHistSections.filter((section) => patMedicalHistory[section.key]?.updatedAt).length
    const progressPercentage = Math.round((completedSections / medHistSections.length) * 100)

    return (
      <div className='desktop-medical-history-overview'>
        <div className='desktop-overview-header'>
          <Title level={1} className='desktop-page-title'>
            Medical History
          </Title>
          <Text className='desktop-page-subtitle'>
            Complete your medical history to help healthcare providers give you the best possible care.
          </Text>

          <div className='desktop-progress-section'>
            <div className='desktop-progress-info'>
              <Text strong>
                Progress: {completedSections} of {medHistSections.length} sections completed
              </Text>
              <div className='desktop-progress-percentage'>{progressPercentage}%</div>
            </div>
            <div className='desktop-progress-bar'>
              <div className='desktop-progress-fill' style={{ width: `${progressPercentage}%` }} />
            </div>
          </div>
        </div>

        <Row gutter={[24, 24]} className='desktop-sections-grid'>
          {medHistSections.map((section) => {
            const isComplete = patMedicalHistory[section.key]?.updatedAt
            return (
              <Col xs={24} sm={12} lg={8} xl={6} key={section.key}>
                <Card
                  className={`desktop-section-card ${isComplete ? 'completed' : 'incomplete'}`}
                  hoverable
                  onClick={() => {
                    setSelectedFacet(section.key)
                  }}
                  style={{
                    '--section-color': section.color,
                    height: '100%'
                  }}
                >
                  <div className='desktop-card-content'>
                    <div className='desktop-card-header'>
                      <div className='desktop-section-icon'>
                        <FontAwesomeIcon icon={section.icon} />
                      </div>
                      <div className='desktop-section-status'>
                        <FontAwesomeIcon
                          icon={isComplete ? faCheckCircle : faXmarkCircle}
                          style={{
                            color: isComplete ? '#10b981' : '#ef4444'
                          }}
                        />
                      </div>
                    </div>

                    <div className='desktop-card-body'>
                      <Title level={4} style={{ color: `rgba(${section.color})`, margin: 0 }}>
                        {section.title}
                      </Title>
                      <Text className='desktop-section-description'>{section.description}</Text>

                      <div className='desktop-section-info'>
                        <Text className='desktop-last-updated'>
                          Last updated:{' '}
                          <span className={isComplete ? 'updated' : 'never-updated'}>
                            {isComplete
                              ? dayjs(patMedicalHistory[section.key].updatedAt).format('DD MMM YYYY')
                              : 'Never'}
                          </span>
                        </Text>
                      </div>
                    </div>
                  </div>
                </Card>
              </Col>
            )
          })}
        </Row>
      </div>
    )
  }

  const renderFacet = () => {
    switch (selectedFacet) {
      case 'lifestyle':
        return <DesktopLifestyle setSaving={setSaving} clientForm={form} setFacet={setSelectedFacet} />
      case 'allergies':
        return (
          <DesktopAllergies
            setSaving={setSaving}
            clientForm={form}
            setFacet={setSelectedFacet}
            saveFunction={handleSave}
            loading={loading}
          />
        )
      case 'mentalHealth':
        return <DesktopMentalHealth setSaving={setSaving} clientForm={form} setFacet={setSelectedFacet} />
      case 'chronicIllnesses':
        return <DesktopChronicIllnesses setSaving={setSaving} clientForm={form} setFacet={setSelectedFacet} />
      case 'meds':
        return <DesktopGeneralMeds setSaving={setSaving} clientForm={form} setFacet={setSelectedFacet} />
      case 'covidInfo':
        return <DesktopCovidInfo setSaving={setSaving} clientForm={form} setFacet={setSelectedFacet} />
      case 'familyHistory':
        return <DesktopFamilyHistory setSaving={setSaving} clientForm={form} setFacet={setSelectedFacet} />
      case 'surgicalHistory':
        return <DesktopSurgeries setSaving={setSaving} clientForm={form} setFacet={setSelectedFacet} />
      default:
        return renderMainView()
    }
  }

  return (
    <>
      <DesktopMedicalHistoryLoadingOverlay
        text='Saving changes...'
        icon={<FontAwesomeIcon icon={faPills} />}
        loading={loading}
        saveLoading={saving}
      />
      <div ref={containerRef} className='desktop-medical-history-content'>
        {renderFacet()}
      </div>
    </>
  )
}

export default DesktopMedicalHistoryForm
